import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import io from 'socket.io-client';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../../ui/dialog";
import { Button } from "../../../../ui/button";
import { ScrollArea } from "../../../../ui/scroll-area";
import { Input } from "../../../../ui/input";
import { MessageCircle, Send, X } from "lucide-react";
import { BACKEND_URL } from "../../../../../config";

const socket = io(BACKEND_URL);

export function ChatSection({ label = "Open Chat", job }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const messageEndRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const hasJoinedRoom = useRef(false);
  
  const { userInfo } = useSelector((state) => state.auth);
  
  // Get user details from Redux store
  const userId = userInfo?.user?.id;
  let clientId = null, providerId = null;
  const jobId = job?._id;
  
  if (userInfo?.user?.role === 'houseOwner') {
    clientId = userId;
    providerId = job.ShovelerInfo[0].ShovelerId || null;
  } else {
    clientId = job.houseOwnerId;
    providerId = userId;
  }

  useEffect(() => {
    if (!jobId || !open) return;

    // Reset state when opening dialog
    setChatMessages([]);
    hasJoinedRoom.current = false;
    
    // Join the chat room for the specific job
    socket.emit('joinRoom', { jobId });
    hasJoinedRoom.current = true;

    // Listen for previous messages
    const handlePreviousMessages = (messages) => {
      setChatMessages(messages || []);
    };

    // Listen for new messages
    const handleNewMessage = (messageData) => {
      setChatMessages((prevMessages) => [...prevMessages, messageData]);
    };

    socket.on('previousMessages', handlePreviousMessages);
    socket.on('newMessage', handleNewMessage);

    // Cleanup on component unmount or when dialog closes
    return () => {
      socket.off('previousMessages', handlePreviousMessages);
      socket.off('newMessage', handleNewMessage);
      
      if (hasJoinedRoom.current) {
        socket.emit('leaveRoom', { jobId });
      }
    };
  }, [jobId, open]);

  useEffect(() => {
    // Scroll to the bottom whenever chatMessages change
    if (messageEndRef.current && chatMessages.length > 0) {
      messageEndRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [chatMessages]);

  const sendMessage = () => {
    if (!message.trim() || !jobId) return;

    const messageData = { 
      jobId, 
      senderId: userId, 
      message, 
      clientId, 
      providerId 
    };
    
    socket.emit('sendMessage', messageData);
    setMessage('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      sendMessage();
    }
  };

  const getDisplayName = (msg) => {
    return msg.senderId === userId ? 'You' : 'Helper';
  };

  const getMessageAlignment = (msg) => {
    return msg.senderId === userId ? 'justify-end' : 'justify-start';
  };

  const getMessageStyles = (msg) => {
    return msg.senderId === userId 
      ? 'bg-black text-white' 
      : 'bg-gray-100 text-gray-900';
  };

  const getTimeStyles = (msg) => {
    return msg.senderId === userId 
      ? 'text-gray-300' 
      : 'text-gray-500';
  };

  // Empty State Component
  const EmptyChatState = () => (
    <div className="flex flex-col items-center justify-center h-full text-center py-8">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <MessageCircle className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        No messages yet
      </h3>
      <p className="text-gray-500 max-w-sm">
        Be the first to start the conversation! Send a message to get things going.
      </p>
    </div>
  );

  // Don't show chat button if no job or user info
  if (!job || !userInfo) {
    return null;
  }

  return (
    <>
      {/* Chat Trigger Button */}
      <Button 
        onClick={() => setOpen(true)}
        variant="outline"
      >
        <MessageCircle className="w-4 h-4 mr-1" />
        {label}
      </Button>

      {/* Chat Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl h-[600px] flex flex-col p-0">
          <DialogHeader className="px-6 py-4 border-b shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Chat - {job.serviceDetail?.service}
              </DialogTitle>
            </div>
          </DialogHeader>

          <div className="flex-1 flex flex-col min-h-0 p-6">
            {/* Messages Area - Fixed height with proper scrolling */}
            <div className="flex-1 min-h-0 mb-4">
              <ScrollArea 
                ref={scrollAreaRef}
                className="h-full pr-4"
              >
                <div className="space-y-4">
                  {chatMessages.length === 0 ? (
                    <EmptyChatState />
                  ) : (
                    chatMessages.map((msg, index) => (
                      <div
                        key={index}
                        className={`flex ${getMessageAlignment(msg)}`}
                      >
                        <div
                          className={`max-w-[80%] p-3 rounded-lg ${getMessageStyles(msg)}`}
                        >
                          <p className="text-sm font-medium mb-1">
                            {getDisplayName(msg)}
                          </p>
                          <p className="text-sm">{msg.message}</p>
                          <p
                            className={`text-xs mt-1 ${getTimeStyles(msg)}`}
                          >
                            {format(new Date(msg.timestamp), 'MMM dd, h:mm a')}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messageEndRef} />
                </div>
              </ScrollArea>
            </div>

            {/* Input Area - Fixed at bottom */}
            <div className="flex gap-2 shrink-0">
              <Input
                placeholder="Type your message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1"
              />
              <Button
                onClick={sendMessage}
                size="sm"
                className="bg-black hover:bg-gray-800"
                disabled={!message.trim()}
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}